/**
 * EmailModal Component
 *
 * Modal dialog for composing and sending gallery link emails to guests.
 * Includes email preview and validation.
 */

import React, { useState } from 'react';
import { X, Mail, Send, AlertCircle, CheckCircle, Eye } from 'lucide-react';
import { sendGalleryLinkEmail, parseAndValidateEmails } from '../../services/emailService';

/**
 * EmailPreview Component
 * Shows a preview of how the email will look
 */
function EmailPreview({ eventData }) {
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date TBD';
    const date = timestamp.toDate();
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className="border border-stone-200 rounded-lg p-6 bg-stone-50">
      <p className="text-sm text-stone-500 mb-4 flex items-center gap-2">
        <Eye className="w-4 h-4" />
        Email Preview
      </p>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Header */}
        <div className="bg-slate-900 p-8 text-center">
          <h1 className="text-3xl font-bold text-gold">MomentBox</h1>
          <p className="text-white text-sm mt-2">Transform Moments into Memories</p>
        </div>

        {/* Body */}
        <div className="p-8">
          <h2 className="text-2xl font-semibold mb-4 text-stone-900">
            {eventData.eventName}
          </h2>
          <p className="text-stone-600 mb-6">
            You're invited to view and relive the special moments captured at this event!
          </p>

          <div className="bg-stone-50 rounded-lg p-4 mb-6 space-y-2">
            <p className="text-sm">
              <strong className="text-gold">📅 Date:</strong>{' '}
              <span className="text-stone-700">{formatDate(eventData.eventDate)}</span>
            </p>
            <p className="text-sm">
              <strong className="text-gold">📍 Location:</strong>{' '}
              <span className="text-stone-700">{eventData.location}</span>
            </p>
            <p className="text-sm">
              <strong className="text-gold">🎉 Host:</strong>{' '}
              <span className="text-stone-700">{eventData.hostName}</span>
            </p>
            <p className="text-sm">
              <strong className="text-gold">🎬 Uploads:</strong>{' '}
              <span className="text-stone-700">{eventData.videoCount}</span>
            </p>
          </div>

          {eventData.guestPassword && (
            <div className="bg-yellow-50 border-l-4 border-gold p-4 mb-6">
              <p className="text-sm">
                <strong>🔒 Gallery Password:</strong>{' '}
                <span className="font-mono">{eventData.guestPassword}</span>
              </p>
            </div>
          )}

          <div className="text-center my-6">
            <div className="inline-block bg-gold text-white px-12 py-3 rounded-lg font-semibold">
              View Gallery
            </div>
          </div>

          <p className="text-stone-500 text-sm italic">
            💫 Share your own memories from this event! Each moment captured helps us remember this special day together.
          </p>
        </div>

        {/* Footer */}
        <div className="bg-stone-50 p-6 text-center border-t border-stone-200">
          <p className="text-stone-600 text-xs">© 2026 MomentBox</p>
          <p className="text-stone-500 text-xs mt-1">
            Premium Digital Guestbook for Special Events
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * EmailModal Component
 * Main modal for composing and sending emails
 */
export default function EmailModal({ isOpen, onClose, eventData }) {
  const [recipients, setRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState(`View memories from ${eventData?.eventName || 'Event'}`);
  const [isSending, setIsSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);

  // Handle recipient input change
  const handleRecipientsChange = (event) => {
    setRecipients(event.target.value);
    // Clear previous status when user types
    if (sendStatus) {
      setSendStatus(null);
    }
  };

  // Toggle preview
  const handleTogglePreview = () => {
    setShowPreview(!showPreview);
  };

  // Close modal
  const handleClose = () => {
    if (!isSending) {
      onClose();
      // Reset state after animation
      setTimeout(() => {
        setRecipients('');
        setEmailSubject(`View memories from ${eventData?.eventName || 'Event'}`);
        setShowPreview(false);
        setSendStatus(null);
      }, 300);
    }
  };

  // Send email
  const handleSendEmail = async () => {
    // Validate input
    if (recipients.trim() === '') {
      setSendStatus({
        type: 'error',
        message: 'Please enter at least one email address'
      });
      return;
    }

    if (emailSubject.trim() === '') {
      setSendStatus({
        type: 'error',
        message: 'Please enter an email subject'
      });
      return;
    }

    // Client-side validation preview
    const validation = parseAndValidateEmails(recipients);
    if (validation.valid.length === 0) {
      setSendStatus({
        type: 'error',
        message: `No valid email addresses found. ${validation.invalid.length > 0 ? `Invalid: ${validation.invalid.join(', ')}` : ''}`
      });
      return;
    }

    // Set loading state
    setIsSending(true);
    setSendStatus(null);

    try {
      // Call email service (which calls Cloud Function)
      const result = await sendGalleryLinkEmail(eventData, recipients, emailSubject);

      if (result.success) {
        // Build success message
        let successMessage = `Email sent successfully to ${result.sentCount} recipient(s)! 🎉`;

        // Add warning about invalid emails if any
        if (result.invalidEmails && result.invalidEmails.length > 0) {
          successMessage += `\n\nNote: ${result.invalidEmails.length} invalid email(s) were skipped: ${result.invalidEmails.join(', ')}`;
        }

        setSendStatus({
          type: 'success',
          message: successMessage
        });

        // Auto-close modal after success
        setTimeout(() => {
          handleClose();
        }, 2500);
      } else {
        // Show error
        let errorMessage = result.error || 'Failed to send email';

        // Add invalid emails info if any
        if (result.invalidEmails && result.invalidEmails.length > 0) {
          errorMessage += `\n\nInvalid emails: ${result.invalidEmails.join(', ')}`;
        }

        setSendStatus({
          type: 'error',
          message: errorMessage
        });
      }
    } catch (error) {
      setSendStatus({
        type: 'error',
        message: `Unexpected error: ${error.message}`
      });
    } finally {
      setIsSending(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <>
      {/* Modal Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        {/* Modal Container */}
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-stone-200">
            <div className="flex items-center gap-3">
              <Mail className="w-6 h-6 text-gold" />
              <h2 className="text-2xl font-semibold text-stone-900">
                Share Gallery via Email
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={isSending}
              className="text-stone-400 hover:text-stone-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
            {/* Tab Buttons */}
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setShowPreview(false)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  !showPreview
                    ? 'bg-gold text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                Compose
              </button>
              <button
                onClick={() => setShowPreview(true)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  showPreview
                    ? 'bg-gold text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                Preview
              </button>
            </div>

            {/* Compose View */}
            {!showPreview && (
              <div>
                {/* Event Info */}
                <div className="bg-stone-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-stone-500 mb-2">Sharing gallery for:</p>
                  <p className="font-semibold text-stone-900 text-lg">
                    {eventData.eventName}
                  </p>
                  <p className="text-sm text-stone-600 mt-1">
                    {eventData.videoCount} upload{eventData.videoCount !== 1 ? 's' : ''} · {eventData.location}
                  </p>
                </div>

                {/* Recipients Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Recipient Email Addresses <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={recipients}
                    onChange={handleRecipientsChange}
                    placeholder="Enter email addresses separated by commas or new lines&#10;&#10;Example:&#10;guest1@example.com&#10;guest2@example.com, guest3@example.com"
                    rows={8}
                    disabled={isSending}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent resize-none font-mono text-sm text-stone-900 placeholder:text-stone-400 disabled:bg-stone-50 disabled:cursor-not-allowed transition-colors"
                  />
                  <p className="text-xs text-stone-500 mt-2">
                    💡 Tip: You can paste multiple emails separated by commas, semicolons, or new lines
                  </p>
                </div>

                {/* Email Subject */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Email Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject"
                    disabled={isSending}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent text-stone-900 placeholder:text-stone-400 disabled:bg-stone-50 disabled:cursor-not-allowed transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Preview View */}
            {showPreview && <EmailPreview eventData={eventData} />}

            {/* Status Messages */}
            {sendStatus && (
              <div
                className={`mt-6 p-4 rounded-lg flex items-start gap-3 ${
                  sendStatus.type === 'success'
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                {sendStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <p
                  className={`text-sm whitespace-pre-line ${
                    sendStatus.type === 'success' ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {sendStatus.message}
                </p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-stone-200 bg-stone-50">
            <button
              onClick={handleClose}
              disabled={isSending}
              className="px-6 py-2.5 border border-stone-300 rounded-lg font-medium text-stone-700 hover:bg-stone-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSendEmail}
              disabled={isSending || recipients.trim() === '' || emailSubject.trim() === ''}
              className="px-6 py-2.5 bg-gold text-white rounded-lg font-medium hover:bg-gold-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>Send Email</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
